import { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Upload, Database, AlertTriangle } from "lucide-react";
import { api } from "../../../lib/api";

export default function BackupRestorePanel({ onChanged }) {
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);
    const [mode, setMode] = useState("merge");

    const backup = async () => {
        setBusy(true);
        try {
            const r = await api.get("/admin/master-ops/backup", { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `master-data-backup-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Backup downloaded");
        } catch (e) {
            toast.error("Backup failed");
        } finally { setBusy(false); }
    };

    const restore = async (file) => {
        if (!file) return;
        const warn = mode === "replace"
            ? "REPLACE mode wipes the entire master_data collection then loads the backup. Continue?"
            : "MERGE mode upserts items by id (safe). Continue?";
        if (!window.confirm(warn)) return;
        setBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const r = await api.post(`/admin/master-ops/restore?mode=${mode}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Restored ${r.data.restored} items (mode=${r.data.mode})`);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Restore failed");
        } finally {
            setBusy(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3" data-testid="md-backup-panel">
            <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-violet-600" />
                <h3 className="font-heading text-sm font-bold text-[#0F172A]">Backup &amp; Restore</h3>
            </div>
            <p className="text-xs text-slate-500">
                Full snapshot of every kind (active + drafts + recycled) in a single JSON file.
                One-click backup; restore in either MERGE (safe upsert by id) or REPLACE (wipe-then-load) mode.
            </p>

            <div className="flex flex-wrap items-center gap-2">
                <button
                    onClick={backup}
                    disabled={busy}
                    data-testid="md-backup-download"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60"
                >
                    <Download className="w-3.5 h-3.5" /> Download backup
                </button>

                <select
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                    data-testid="md-restore-mode"
                    className="text-xs px-2 py-2 rounded-lg border border-slate-200"
                >
                    <option value="merge">Restore mode: MERGE (safe)</option>
                    <option value="replace">Restore mode: REPLACE (wipe-then-load)</option>
                </select>

                <button
                    onClick={() => fileRef.current?.click()}
                    disabled={busy}
                    data-testid="md-restore-upload"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-slate-200 hover:bg-slate-50"
                >
                    <Upload className="w-3.5 h-3.5" /> Choose backup file
                </button>
                <input
                    ref={fileRef}
                    type="file"
                    accept=".json,application/json"
                    className="hidden"
                    onChange={(e) => restore(e.target.files?.[0])}
                    data-testid="md-restore-file-input"
                />
            </div>

            {mode === "replace" && (
                <div className="text-[11px] px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>REPLACE mode is destructive — every existing master_data document is removed before the backup is applied.</span>
                </div>
            )}
        </div>
    );
}
